"use client";
import React from 'react';
import Link from 'next/link';
import { Home, Search, ArrowLeft, Package, ShoppingBag } from 'lucide-react';

const NotFound = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#422819] via-[#846342] to-[#a58b6a] flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto text-center">
        {/* Animated 404 */}
        <div className="relative mb-8">
          <h1 className="text-9xl md:text-[12rem] font-bold text-[#d8c6a2] opacity-20 select-none">
            404
          </h1>
          <div className="absolute inset-0 flex items-center justify-center">
            <Package className="w-24 h-24 md:w-32 md:h-32 text-[#d8c6a2] animate-bounce" />
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-2xl border border-[#d8c6a2]/20">
          <h2 className="text-3xl md:text-4xl font-bold text-[#422819] mb-4">
            Oops! Page Not Found
          </h2>
          
          <p className="text-lg text-[#846342] mb-8 leading-relaxed">
            It looks like the page you're looking for has wandered off. 
            Don't worry, even our best products sometimes get misplaced!
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Link 
              href="/"
              className="group flex items-center gap-2 bg-amber-900 hover:bg-[#846342] text-white px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Home className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              Back to Home
            </Link>
            
            <Link 
              href="/shop"
              className="group flex items-center gap-2 bg-amber-900 hover:bg-[#846342] text-white px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <ShoppingBag className="w-5 h-5 group-hover:bounce transition-transform" />
              Browse Products
            </Link>
          </div>

          {/* Search Suggestion */}
          <div className="border-t border-[#d8c6a2]/30 pt-6">
            <p className="text-[#846342] mb-4">
              Looking for something specific? Try searching:
            </p>
            
            <div className="flex max-w-md mx-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#a58b6a] w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-3 border border-[#d8c6a2] rounded-l-full focus:outline-none focus:ring-2 focus:ring-[#a58b6a] focus:border-transparent"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      window.location.href = `/explore?search=${encodeURIComponent(e.target.value.trim())}`;
                    }
                  }}
                />
              </div>
              <button 
                onClick={(e) => {
                  const input = e.target.previousElementSibling.querySelector('input');
                  if (input.value.trim()) {
                    window.location.href = `/shop/${encodeURIComponent(input.value.trim())}`;
                  }
                }}
                className="bg-amber-900 hover:bg-[#846342] text-white px-6 py-3 rounded-r-full transition-colors duration-300"
              >
                Search
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-8 pt-6 border-t border-[#d8c6a2]/30">
            <p className="text-[#846342] mb-4">Quick Links:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                href="/categories"
                className="text-[#a58b6a] hover:text-[#422819] transition-colors duration-300 hover:underline"
              >
                Categories
              </Link>
              <span className="text-[#d8c6a2]">•</span>
              <Link 
                href="/new-arrivals"
                className="text-[#a58b6a] hover:text-[#422819] transition-colors duration-300 hover:underline"
              >
                New Arrivals
              </Link>
              <span className="text-[#d8c6a2]">•</span>
              <Link 
                href="/contact-us"
                className="text-[#a58b6a] hover:text-[#422819] transition-colors duration-300 hover:underline"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Go Back Option */}
        <button
          onClick={() => window.history.back()}
          className="mt-6 flex items-center gap-2 text-[#d8c6a2] hover:text-white transition-colors duration-300 mx-auto group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Go back to previous page
        </button>
      </div>
    </div>
  );
};

export default NotFound;